import React from "react";
import {
  AssignmentStatus,
  kEmployeeAssignmentStatusID,
  kEmployeeID,
  kEmployeePositionID
} from "@coreapp/constants";
import { Checkbox, Flex, OverflowText, withKeyboardSelect } from "@theorgchartdev/storybook";
import { EmployeeDetailIcon } from "./EmployeeDetailIcon";
import { GetDoc, ReRenderCanvas } from "@/Utils/StateLib";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { isAssignmentStatus } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/AssignmentStatusPill";
import { useNavigationContext } from "@/providers/NavigationProvider";
import EditableItemGroup, {
  Action
} from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { QRecordLookupRFC } from "@coreapp/CoreApp/QRecordLookupRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type EmployeeDetailProps = {
  checked: boolean;
  onSelect: (
    e: React.MouseEvent | React.KeyboardEvent | React.ChangeEvent<HTMLInputElement>
  ) => void;

  employee?: Record<string, unknown>;
  refObj?: QObjDBObject;
  showCheckbox?: boolean;
};

export const fetchAndResolveObjDBObject = (
  id: string,
  callback: (obj: QObjDBObject | null) => void
) => {
  const doc = GetDoc();
  if (doc?.IsCustomChart()) return; // Custom charts should have all their DB objects loaded.
  const wsMgr = doc?.GetWSMgr();

  const setRecordFallbackCallback = (r: any) => {
    let resObject = typeof r === "string" ? JSON.parse(r) : r;

    const newDBObject = new QObjDBObject();
    const objClass = doc?.GetBoxClass();

    try {
      newDBObject.InitObject(objClass);
      objClass?.ProcessObject(newDBObject, resObject.data[0]);

      callback(newDBObject);
    } catch (error) {
      console.error("Error fetching object:", error);
      callback(null);
    }
  };

  if (doc?.IsPlanChart()) {
    wsMgr
      ?.GetPlanMgr()
      ?.LoadRecords({ recordIDs: [id], applySearchOverrides: true }, setRecordFallbackCallback);
  } else {
    var recordLookup = new QRecordLookupRFC();
    recordLookup.InitRecordLookup(null, setRecordFallbackCallback as any);
    recordLookup.SetParams(
      wsMgr?.GetAccountName(),
      [id],
      wsMgr?.GetDoc()?.GetChartID(),
      wsMgr?.GetUserName(),
      false
    );
    Globals._rfcMgr.QueueRFC(recordLookup);
  }
};

const getEmployeeID = (employee: Record<string, unknown> | undefined): string | undefined => {
  const employeeIDField = GetDoc()?.GetBoxClass()?.GetFieldDefByID(kEmployeeID)?.GetFieldName();
  return employee?.[employeeIDField || ""] as string | undefined;
};

const getAssocObjDBIfExists = (holderIDValue: string): QObjDBObject | null => {
  const doc = GetDoc();
  const objClass = doc?.GetBoxClass();
  const recordIDFieldDef = objClass?.GetRecordIDFieldDef();

  return objClass?.FindObject(recordIDFieldDef, holderIDValue, true) ?? null;
};

export const EmployeeDetail = React.memo(function EmployeeDetail(props: EmployeeDetailProps) {
  const { LaunchUIElement } = useNavigationContext();

  const handleProfileOpen = () => {
    const holderIDValue = getEmployeeID(props.employee) ?? "";
    let target = props.refObj ?? getAssocObjDBIfExists(holderIDValue);

    if (target) {
      LaunchUIElement("profile", true, {
        target: target,
        ignoreSelection: true
      });
    } else {
      fetchAndResolveObjDBObject(holderIDValue as string, resolvedObj => {
        if (resolvedObj) {
          LaunchUIElement("profile", true, {
            target: resolvedObj,
            ignoreSelection: true
          });
        }
      });
    }
  };

  const handleNavigateButton = () => {
    const holder = props.employee;

    // The employee ID name is not normalized, but Position ID is.
    const holderID = getEmployeeID(holder);
    const positionID = holder?.[kEmployeePositionID];

    let wsMgr = GetDoc()?.GetWSMgr();
    const canvasPanel = wsMgr?.fCanvasPanel;
    canvasPanel?.AddToInitialHighlight(holderID);

    let cmdElement = {
      targetID: positionID,
      queryID: positionID,
      dbObjRef: positionID
    };
    wsMgr?.DoNavToBox(cmdElement, undefined, false, false, false);
    ReRenderCanvas();
  };

  const handleRecordDetails = () => {
    const holderIDValue = getEmployeeID(props.employee) ?? "";
    const wsMgr = GetDoc()?.GetWSMgr();

    let dbObj = props.refObj ?? getAssocObjDBIfExists(holderIDValue);

    if (!dbObj) {
      fetchAndResolveObjDBObject(holderIDValue as string, resolvedObj => {
        if (resolvedObj) {
          wsMgr?.ShowRecordInfo(null, resolvedObj);
        }
      });
    } else {
      wsMgr?.ShowRecordInfo(null, dbObj);
    }
  };

  const getActions = (): Action[] => {
    let actions: Action[] = [];
    const isCustomChart = GetDoc()?.IsCustomChart();

    const employeeIsAssigned =
      getAssignmentStatus(props.employee, props.refObj) === AssignmentStatus.Assigned;

    if (employeeIsAssigned) {
      actions.push({
        iconName: "sitemap",
        onClick: handleNavigateButton,
        dataTestId: chartModeTestId("sidebar", "employee-list-item-navigate-btn")
      });
    }

    actions.push({
      iconName: "address-card",
      onClick: handleProfileOpen,
      dataTestId: chartModeTestId("sidebar", "employee-list-item-profile-btn")
    });

    if (isCustomChart) {
      actions.push({
        iconName: "circle-info",
        onClick: handleRecordDetails,
        dataTestId: chartModeTestId("sidebar", "employee-list-item-details-btn")
      });
    }

    return actions;
  };

  const employeeLabel = getEmployeeLabel(props.employee, props.refObj);

  return (
    <div
      data-testid={`employee-list-item-${employeeLabel}`}
      tabIndex={0}
      onClick={props.onSelect}
      onKeyDown={withKeyboardSelect(props.onSelect)}
    >
      <Flex gap={"8px"} direction={"column"} width={"100%"} justifyContent={"space-between"}>
        <EditableItemGroup spaceBetween={true} actions={getActions()}>
          <Flex alignItems="center">
            {props?.showCheckbox && (
              <Checkbox
                onToggle={props.onSelect}
                checked={props.checked}
                size="medium"
                label="&nbsp;"
                data-testid={chartModeTestId("sidebar", "employee-list-item-checkbox")}
              />
            )}
            <Flex gap={"4px"} alignItems="center" width="100%">
              <EmployeeDetailIcon variant={getAssignmentStatus(props.employee, props.refObj)} />
              <OverflowText>{employeeLabel}</OverflowText>
            </Flex>
          </Flex>
        </EditableItemGroup>
      </Flex>
    </div>
  );
});

const getEmployeeLabel = (employee?: Record<string, unknown>, refObj?: QObjDBObject): string => {
  const doc = GetDoc();
  const objDb = doc?.GetObjDB();
  const boxClass = objDb?.GetClassByName("Box");
  const nameFieldDef = boxClass?.GetFieldDefByID("Name");
  if (!nameFieldDef) {
    return "--";
  }

  let value: string;
  if (refObj) {
    value = String(refObj.GetFieldValue(nameFieldDef) || "--");
  } else {
    const nameFieldName = nameFieldDef.GetFieldName();
    value = String(employee?.[nameFieldName] || "--");
  }

  return sanitizeBlockedValue(value);
};

const getAssignmentStatus = (
  employee: EmployeeDetailProps["employee"],
  refObj: EmployeeDetailProps["refObj"]
): ValueOf<typeof AssignmentStatus> => {
  let assignmentStatus: unknown = AssignmentStatus.Unassigned;

  if (employee) {
    assignmentStatus = employee[kEmployeeAssignmentStatusID] as unknown;
  }

  if (refObj) {
    assignmentStatus = refObj.SmartGetFieldValue(kEmployeeAssignmentStatusID) as unknown;
  }

  if (!isAssignmentStatus(assignmentStatus)) {
    return AssignmentStatus.Unassigned;
  }

  return assignmentStatus;
};
